import { Outlet, NavLink } from "react-router-dom";
import {
  Home, TrendingUp, Calendar, FolderOpen, MessageSquare,
  DollarSign, Users, Monitor, UserCircle, Video
} from "lucide-react";
import { NotificationBell } from "@/components/notifications";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageSelector } from "@/components/ui/language-selector";
import { RoleSwitcher } from "@/components/layout/RoleSwitcher";
import { SignOutButton } from "@/components/layout/SignOutButton";

const navigation = [
  { nameEn: "Dashboard",    nameFr: "Tableau de bord", href: "/dashboard/lender",                    icon: Home,         end: true  },
  { nameEn: "Pipeline",     nameFr: "Pipeline",        href: "/dashboard/lender/pipeline",            icon: TrendingUp               },
  { nameEn: "Loan Offers",  nameFr: "Mes Offres",      href: "/dashboard/lender/loan-offers",         icon: DollarSign               },
  { nameEn: "Deal Monitor", nameFr: "Deal Monitor",    href: "/listings",                             icon: Monitor                  },
];

const tools = [
  { nameEn: "Consultants",       nameFr: "Consultants",       href: "/dashboard/lender/consultants", icon: Users         },
  { nameEn: "Calendar",          nameFr: "Calendrier",         href: "/dashboard/lender/calendar",    icon: Calendar      },
  { nameEn: "Video Conference",  nameFr: "Visioconférence",    href: "/dashboard/lender/video",       icon: Video         },
  { nameEn: "Documents",         nameFr: "Documents",          href: "/dashboard/lender/documents",   icon: FolderOpen    },
  { nameEn: "Messages",          nameFr: "Messages",           href: "/dashboard/lender/messages",    icon: MessageSquare },
];

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  [
    "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all duration-150 relative",
    isActive
      ? "bg-[#CEAB65]/10 text-[#CEAB65] font-medium before:absolute before:left-0 before:top-1 before:bottom-1 before:w-0.5 before:bg-[#CEAB65] before:rounded-r"
      : "text-white/55 hover:bg-white/6 hover:text-white/85",
  ].join(" ");

export default function LenderLayout() {
  const { language } = useLanguage();
  const isFr = language === "fr";

  return (
    <div className="min-h-screen bg-background flex">
      {/* ── Sidebar ─────────────────────────────────────── */}
      <aside className="w-56 flex flex-col flex-shrink-0" style={{ background: "#1D314D" }}>

        {/* Logo */}
        <div className="px-5 pt-5 pb-4">
          {/* Fine gold gradient line above logo */}
          <div
            className="h-px mb-4 rounded"
            style={{ background: "linear-gradient(90deg, #CEAB65 0%, rgba(206,171,101,0.1) 100%)" }}
          />
          <img
            src="/noBgColor.png"
            alt="AcquizGroup"
            className="h-7 w-auto"
            style={{ filter: "brightness(0) invert(1)" }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
              const span = document.createElement("span");
              span.textContent = "AcquizGroup™";
              span.style.cssText = "color:#CEAB65;font-size:13px;font-weight:500;letter-spacing:1px;";
              (e.target as HTMLImageElement).parentNode?.appendChild(span);
            }}
          />
          <p className="text-[9px] tracking-widest uppercase mt-1.5" style={{ color: "rgba(255,255,255,0.3)" }}>
            {isFr ? "Portail Prêteur" : "Lender Portal"}
          </p>
          {/* Gold fade line below */}
          <div
            className="h-px mt-4"
            style={{ background: "linear-gradient(90deg, rgba(206,171,101,0.35) 0%, transparent 80%)" }}
          />
        </div>

        {/* Main nav */}
        <nav className="flex-1 px-3 py-2 overflow-y-auto space-y-0.5">
          <p className="text-[8.5px] tracking-widest uppercase px-2 py-2" style={{ color: "rgba(255,255,255,0.25)" }}>
            {isFr ? "Principal" : "Main"}
          </p>
          {navigation.map((item) => (
            <NavLink key={item.href} to={item.href} end={item.end} className={navLinkClass}>
              <item.icon className="w-4 h-4 flex-shrink-0" />
              <span>{isFr ? item.nameFr : item.nameEn}</span>
            </NavLink>
          ))}

          <p className="text-[8.5px] tracking-widest uppercase px-2 pt-4 pb-2" style={{ color: "rgba(255,255,255,0.25)" }}>
            {isFr ? "Outils" : "Tools"}
          </p>
          {tools.map((item) => (
            <NavLink key={item.href} to={item.href} className={navLinkClass}>
              <item.icon className="w-4 h-4 flex-shrink-0" />
              <span>{isFr ? item.nameFr : item.nameEn}</span>
            </NavLink>
          ))}
        </nav>

        {/* Footer user */}
        <div className="px-4 py-3" style={{ borderTop: "0.5px solid rgba(255,255,255,0.07)" }}>
          <NavLink to="/lender/profile" className="flex items-center gap-2.5 group">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: "rgba(206,171,101,0.15)", border: "1px solid rgba(206,171,101,0.25)" }}
            >
              <UserCircle className="w-4 h-4" style={{ color: "#CEAB65" }} />
            </div>
            <div>
              <p className="text-[11px] font-medium group-hover:text-white/90 transition-colors"
                 style={{ color: "rgba(255,255,255,0.75)" }}>
                {isFr ? "Mon Profil" : "My Profile"}
              </p>
              <p className="text-[9px]" style={{ color: "rgba(255,255,255,0.35)" }}>
                {isFr ? "Paramètres" : "Settings"}
              </p>
            </div>
          </NavLink>
        </div>
      </aside>

      {/* ── Main area ───────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header
          className="h-14 flex items-center justify-between px-6 flex-shrink-0 relative"
          style={{
            background: "var(--color-background-primary, white)",
            borderBottom: "0.5px solid var(--color-border-tertiary)",
          }}
        >
          {/* Subtle gold underline accent */}
          <div
            className="absolute bottom-0 left-6 right-6 h-px"
            style={{ background: "linear-gradient(90deg, #CEAB65 0%, rgba(206,171,101,0) 50%)", opacity: 0.4 }}
          />

          <div className="flex items-center gap-2.5">
            <h1 className="text-[15px] font-medium tracking-tight" style={{ color: "var(--color-text-primary)" }}>
              {isFr ? "Portail Prêteur" : "Lender Portal"}
            </h1>
            <span
              className="text-[10px] px-2.5 py-0.5 rounded-full"
              style={{
                background: "rgba(29,49,77,0.07)",
                color: "#1D314D",
                border: "0.5px solid rgba(29,49,77,0.15)",
                letterSpacing: "0.5px",
              }}
            >
              Lender
            </span>
          </div>

          <div className="flex items-center gap-2.5">
            <RoleSwitcher />
            <LanguageSelector />
            <NotificationBell />
            <SignOutButton />
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 overflow-y-auto bg-background">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
